import { z } from 'zod';
import {
  ACL_COMPANY_PRINCIPAL,
  ACL_PRINCIPAL_LIMITS,
  canonicalizeAclPrincipals,
} from '../constants/acl-principals';
import { companyIdSchema } from './company';
import { chunkAnchorSchema } from './rag-anchors';

const GUID_SEGMENT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHAREPOINT_SITE_ID_PATTERN = /^[a-z0-9][a-z0-9.,_-]*$/i;
const SHAREPOINT_GROUP_ID_PATTERN = /^[1-9][0-9]{0,9}$/;

function normalizeAclPrincipal(principal: string): string {
  return principal === ACL_COMPANY_PRINCIPAL ? principal : principal.toLowerCase();
}

function isValidAclPrincipal(principal: string): boolean {
  if (principal === ACL_COMPANY_PRINCIPAL) return true;

  const entraMatch = /^(entra):(u|g):(.+)$/i.exec(principal);
  if (entraMatch) {
    const identifier = entraMatch[3];
    return identifier !== undefined
      && !identifier.includes(':')
      && !identifier.includes('#')
      && GUID_SEGMENT_PATTERN.test(identifier);
  }

  const siteGroupMatch = /^sp:sg:([^#]+)#([^#]+)$/i.exec(principal);
  if (!siteGroupMatch) return false;

  const siteId = siteGroupMatch[1];
  const groupId = siteGroupMatch[2];
  if (siteId === undefined || groupId === undefined) return false;
  if (siteId.includes(':') || siteId.includes('#')) return false;
  if (!SHAREPOINT_SITE_ID_PATTERN.test(siteId) || !SHAREPOINT_GROUP_ID_PATTERN.test(groupId)) {
    return false;
  }

  return Number(groupId) <= 2_147_483_647;
}

export const aclPrincipalSchema = z.string({
  error: 'Le principal ACL est invalide.',
})
  .min(1, { error: 'Le principal ACL est requis.' })
  .max(ACL_PRINCIPAL_LIMITS.maxLength, {
    error: `Le principal ACL ne peut pas dépasser ${ACL_PRINCIPAL_LIMITS.maxLength} caractères.`,
  })
  .refine(isValidAclPrincipal, { error: 'Le format du principal ACL est invalide.' })
  .transform(normalizeAclPrincipal);

export const aclPrincipalsSchema = z.array(aclPrincipalSchema, {
  error: 'Les principals ACL sont invalides.',
})
  .min(1, { error: 'Au moins un principal ACL est requis.' })
  .max(ACL_PRINCIPAL_LIMITS.maxEntries, {
    error: `Les principals ACL sont limités à ${ACL_PRINCIPAL_LIMITS.maxEntries} entrées.`,
  })
  .transform(canonicalizeAclPrincipals);

export type AclPrincipal = z.infer<typeof aclPrincipalSchema>;

// rag_chunks.metadata was previously modeled as z.record(z.string(), z.unknown()).
// Keep arbitrary legacy metadata keys while typing the new optional citation anchor.
export const ragChunkMetadataSchema = z.object({
  anchor: chunkAnchorSchema.optional().nullable(),
}).catchall(z.unknown()).default({});

/**
 * Représentation d'un chunk retrouvé par le retrieval hybride.
 * Source primaire = document indexé (rag_chunks).
 */
export const ragRetrievedChunkSchema = z.object({
  anchor: chunkAnchorSchema.optional().nullable(),
  chunkId: z.uuid(),
  documentId: z.uuid(),
  /**
   * @deprecated PageIndex legacy id, kept for citation backward-compat
   * during hybrid_v2 migration. Will be removed once all clients use chunkId.
   */
  ragDocumentId: z.string(),
  /** -1 marks synthetic PageIndex tool passages (not a row in rag_chunks). */
  chunkIndex: z.number().int().min(-1),
  pageStart: z.number().int().positive().nullable(),
  pageEnd: z.number().int().positive().nullable(),
  sectionPath: z.array(z.string()).default([]),
  content: z.string().min(1),
  rerankScore: z.number(),
  scores: z.object({
    vector: z.number().nullable(),
    lexical: z.number().nullable(),
    rrf: z.number(),
    rerank: z.number(),
  }),
  metadata: ragChunkMetadataSchema,
});

export type RagRetrievedChunk = z.infer<typeof ragRetrievedChunkSchema>;

/**
 * Tableau extrait inclus dans la réponse retrieval quand le score lexical
 * sur ses cellules dépasse le seuil de pertinence.
 */
export const ragRetrievedTableSchema = z.object({
  tableId: z.uuid(),
  documentId: z.uuid(),
  pageNumber: z.number().int().positive(),
  caption: z.string().nullable(),
  tableType: z.enum([
    'error_codes',
    'parts_list',
    'maintenance_schedule',
    'specs',
    'generic',
  ]),
  rows: z.array(z.record(z.string(), z.string())),
  rawText: z.string(),
  lexicalScore: z.number(),
});

export type RagRetrievedTable = z.infer<typeof ragRetrievedTableSchema>;

export const conversationTurnSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().min(1).max(4000),
});

export type ConversationTurn = z.infer<typeof conversationTurnSchema>;

export const queryIntentSchema = z.enum([
  'lookup',
  'navigation',
  'procedural',
  'troubleshooting',
  'comparison',
  'synthesis',
  'clarification',
]);

export type QueryIntent = z.infer<typeof queryIntentSchema>;

export const queryUnderstandingResultSchema = z.object({
  originalQuery: z.string(),
  rewrittenQuery: z.string(),
  intent: queryIntentSchema,
  confidence: z.number().min(0).max(1),
  hasStructuralReference: z.boolean(),
  detectedEntities: z.array(z.string()).default([]),
});

export type QueryUnderstandingResult = z.infer<typeof queryUnderstandingResultSchema>;

export const ragGroundingResultSchema = z.object({
  grounded: z.boolean(),
  missingInfo: z.array(z.string().max(200)).max(5).default([]),
  confidence: z.number().min(0).max(1),
});

export type RagGroundingResult = z.infer<typeof ragGroundingResultSchema>;

export const ragRetrievalRequestSchema = z.object({
  query: z.string().min(1).max(2000),
  companyId: companyIdSchema,
  principals: aclPrincipalsSchema,
  aclEpoch: z.number({ error: 'L’époque ACL est invalide.' }).int({
    error: 'L’époque ACL doit être un entier.',
  }).nonnegative({ error: 'L’époque ACL ne peut pas être négative.' }),
  documentIds: z.array(z.uuid()).optional(),
  conversationExcerpt: z.array(conversationTurnSchema).max(10).default([]),
  topK: z.number().int().positive().max(20).default(5),
});

export type RagRetrievalRequest = z.infer<typeof ragRetrievalRequestSchema>;
export type RagRetrievalRequestInput = z.input<typeof ragRetrievalRequestSchema>;

export const ragRetrievedResponseChunkSchema = ragRetrievedChunkSchema.extend({
  source: z.enum(['hybrid', 'pageindex_tool']).default('hybrid'),
  isVerifiable: z.boolean().default(true),
});

export type RagRetrievedResponseChunk = z.infer<typeof ragRetrievedResponseChunkSchema>;

export const ragRetrievalResponseSchema = z.object({
  query: z.string(),
  engineVersion: z.literal('hybrid_v2'),
  understanding: queryUnderstandingResultSchema,
  chunks: z.array(ragRetrievedResponseChunkSchema),
  tables: z.array(ragRetrievedTableSchema).default([]),
  meta: z.object({
    pageindexToolTriggered: z.boolean(),
    pageindexToolDocumentId: z.uuid().nullable(),
    pageindexToolDurationMs: z.number().int().nonnegative().nullable(),
    cacheHit: z.boolean(),
    selfReflectionApplied: z.boolean(),
    // E1-D6 : true quand la source vectorielle a échoué et que la réponse est servie
    // en lexical + tables seulement (défaut false = rétrocompatible avec les payloads antérieurs).
    embeddingDegraded: z.boolean().default(false),
  }),
  totalDocumentsSearched: z.number().int().nonnegative(),
  totalChunksConsidered: z.number().int().nonnegative(),
  timingsMs: z.object({
    queryUnderstanding: z.number(),
    embedding: z.number(),
    vectorSearch: z.number(),
    lexicalSearch: z.number(),
    tableSearch: z.number(),
    fusion: z.number(),
    pageindexTool: z.number(),
    rerank: z.number(),
    total: z.number(),
  }),
  // TODO(PR 5): replace z.unknown() once RagUsageEvent is promoted to @shared.
  usageEvents: z.array(z.unknown()).default([]),
});

export type RagRetrievalResponse = z.infer<typeof ragRetrievalResponseSchema>;
